"use client";

/**
 * Watchlist-level market depth subscription owner.
 *
 * Responsibilities:
 *  - Maintain exactly one active SignalR market-depth subscription, mirroring
 *    the selected chart slot's symbol (`useChartStore3.slots[panelId]`).
 *  - Skip symbols where `depthOfMarket <= 0` (the broker does not provide DOM).
 *  - Preserve the previous symbol's cached book in Zustand so the chart panel
 *    can render stale-but-instant data on the next revisit.
 *  - Survive rapid symbol switching: a debounce on the change plus a
 *    monotonically-increasing request id make sure stale async completions
 *    cannot overwrite the latest selected symbol's lifecycle.
 *  - Independent of UI visibility: toggling the chart's market-depth panel
 *    does NOT trigger any subscribe/unsubscribe traffic.
 */

import { useEffect, useRef } from "react";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useMarketDepthStore } from "../stores/useMarketDepthStore";

/**
 * Debounce window for symbol changes. Short enough to feel instant on a
 * deliberate click, long enough to absorb keyboard arrow scrolling through a
 * watchlist without firing dozens of subscribe/unsubscribe pairs.
 */
const SYMBOL_CHANGE_DEBOUNCE_MS = 120;

export function useSelectedSymbolMarketDepthSubscription(options?: {
  /** When false, release subscription and do not drive activeSymbol (multi-chart non-owning panes). */
  enabled?: boolean;
  /** Chart slot 0–3; default 0. */
  panelId?: ChartPanelId;
}): void {
  const enabled = options?.enabled !== false;
  const panelId: ChartPanelId = options?.panelId ?? 0;

  const { client, connectionStatus } = useVybeTraderClient();
  const selectedSymbolCurrency = useChartStore3((state) =>
    enabled ? state.slots[panelId].selectedSymbol?.currency ?? null : null,
  );
  const selectedSymbolDepthOfMarket = useChartStore3((state) =>
    enabled ? state.slots[panelId].selectedSymbol?.depthOfMarket ?? 0 : 0,
  );
  const isConnected = connectionStatus === "connected";

  // Tracks the symbol whose subscription we currently own (server-side).
  const activeSubscribedRef = useRef<string | null>(null);
  // Generation counter to ignore stale async results.
  const generationRef = useRef(0);
  // Pending debounce timer for symbol changes.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!client) return;
    if (!isConnected) {
      // The server side has lost any subscription we held. Drop local
      // ownership so the next connection re-subscribes the chart symbol.
      // Cached snapshots in the store stay intact for instant repaint.
      if (activeSubscribedRef.current) {
        useMarketDepthStore.getState().markIdle(activeSubscribedRef.current);
      }
      activeSubscribedRef.current = null;
      generationRef.current++;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    const desiredSymbol =
      selectedSymbolCurrency && selectedSymbolDepthOfMarket > 0
        ? selectedSymbolCurrency
        : null;

    // Always reflect the chart's current target in the store, even if we
    // skip the subscribe (e.g. depthOfMarket == 0).
    useMarketDepthStore.getState().setActiveSymbol(desiredSymbol);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // No-op if we are already aligned.
    if (activeSubscribedRef.current === desiredSymbol) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;

      const generation = ++generationRef.current;
      const previous = activeSubscribedRef.current;
      const next = desiredSymbol;

      // Optimistically own the new symbol so re-entrancy stays consistent.
      activeSubscribedRef.current = next;

      // Tear down the previous subscription, if any. Snapshot is preserved
      // by the store's `applyUnsubscribeAck` / `markIdle` helpers.
      if (previous && previous !== next) {
        client.unsubscribeMarketDepth(previous).catch((error: unknown) => {
          console.warn(
            "[MarketDepthOwner] unsubscribeMarketDepth failed:",
            previous,
            error
          );
        });
        // Optimistically mark the previous symbol idle locally so the UI does
        // not show "subscribing" for it if the user revisits before the ack.
        useMarketDepthStore.getState().markIdle(previous);
      }

      if (!next) {
        return;
      }

      useMarketDepthStore.getState().setSubscribing(next);
      client.subscribeMarketDepth(next).catch((error: unknown) => {
        // Only roll back ownership if no newer generation has taken over.
        if (
          generation === generationRef.current &&
          activeSubscribedRef.current === next
        ) {
          activeSubscribedRef.current = null;
        }
        console.warn(
          "[MarketDepthOwner] subscribeMarketDepth failed:",
          next,
          error
        );
      });
    }, SYMBOL_CHANGE_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [
    client,
    isConnected,
    enabled,
    panelId,
    selectedSymbolCurrency,
    selectedSymbolDepthOfMarket,
  ]);

  // Tear down on unmount/disconnect: release the active server subscription
  // but leave cached snapshots in place so a re-mount can reuse them.
  useEffect(() => {
    return () => {
      const previous = activeSubscribedRef.current;
      activeSubscribedRef.current = null;
      generationRef.current++;
      if (previous && client) {
        client.unsubscribeMarketDepth(previous).catch((error: unknown) => {
          console.warn(
            "[MarketDepthOwner] cleanup unsubscribeMarketDepth failed:",
            previous,
            error
          );
        });
        useMarketDepthStore.getState().markIdle(previous);
      }
      useMarketDepthStore.getState().setActiveSymbol(null);
    };
  }, [client]);
}
